/**
 * User preferences stored in localStorage — theme + currency.
 *
 * No server roundtrip: settings live in the browser. The `useTheme` hook
 * resolves `'system'` against the OS preference and writes the chosen
 * theme back onto `<html data-theme="…">` so Tailwind's
 * `[data-theme="dark"]` selector picks it up.
 */

import { useCallback, useEffect, useState } from "react";

const THEME_KEY = "fellesen.theme";
const CURRENCY_KEY = "fellesen.currency";
const EXPENSE_LAYOUT_KEY = "fellesen.expenseLayout";
const FAVORITE_HOUSEHOLDS_KEY = "fellesen.favoriteHouseholds";
const EXPENSES_COLLAPSED_KEY = "fellesen.expensesCollapsed";

export type ThemePref = "light" | "dark" | "system";
export type Currency = "NOK";
export type ExpenseLayout = "card" | "table";

const THEME_VALUES: ThemePref[] = ["light", "dark", "system"];
const CURRENCY_VALUES: Currency[] = ["NOK"];
const EXPENSE_LAYOUT_VALUES: ExpenseLayout[] = ["card", "table"];

function readTheme(): ThemePref {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(THEME_KEY);
  return THEME_VALUES.includes(stored as ThemePref) ? (stored as ThemePref) : "system";
}

function readCurrency(): Currency {
  if (typeof window === "undefined") return "NOK";
  const stored = window.localStorage.getItem(CURRENCY_KEY);
  return CURRENCY_VALUES.includes(stored as Currency) ? (stored as Currency) : "NOK";
}

function resolveTheme(pref: ThemePref): "light" | "dark" {
  if (pref !== "system") return pref;
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: "light" | "dark") {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = resolved;
}

/**
 * Apply the persisted theme immediately, before React mounts, so the page
 * doesn't flash light-then-dark. Call from `main.tsx`.
 */
export function initTheme() {
  applyTheme(resolveTheme(readTheme()));
}

export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(readTheme);

  useEffect(() => {
    applyTheme(resolveTheme(pref));
  }, [pref]);

  useEffect(() => {
    if (pref !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme(resolveTheme("system"));
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pref]);

  const set = useCallback((next: ThemePref) => {
    window.localStorage.setItem(THEME_KEY, next);
    setPref(next);
  }, []);

  return { theme: pref, resolved: resolveTheme(pref), setTheme: set };
}

function readExpenseLayout(): ExpenseLayout {
  if (typeof window === "undefined") return "card";
  const stored = window.localStorage.getItem(EXPENSE_LAYOUT_KEY);
  return EXPENSE_LAYOUT_VALUES.includes(stored as ExpenseLayout)
    ? (stored as ExpenseLayout)
    : "card";
}

function readFavoriteHouseholds(): Set<string> {
  if (typeof window === "undefined") return new Set();
  const raw = window.localStorage.getItem(FAVORITE_HOUSEHOLDS_KEY);
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((v) => typeof v === "string")) : new Set();
  } catch {
    return new Set();
  }
}

export function useFavoriteHouseholds() {
  const [favorites, setFavorites] = useState<Set<string>>(readFavoriteHouseholds);

  const toggle = useCallback((householdId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(householdId)) next.delete(householdId);
      else next.add(householdId);
      window.localStorage.setItem(FAVORITE_HOUSEHOLDS_KEY, JSON.stringify(Array.from(next)));
      return next;
    });
  }, []);

  const isFavorite = useCallback((householdId: string) => favorites.has(householdId), [favorites]);

  return { favorites, toggleFavorite: toggle, isFavorite };
}

export function useExpenseLayout() {
  const [layout, setLayoutState] = useState<ExpenseLayout>(readExpenseLayout);

  const set = useCallback((next: ExpenseLayout) => {
    window.localStorage.setItem(EXPENSE_LAYOUT_KEY, next);
    setLayoutState(next);
  }, []);

  return { layout, setLayout: set };
}

function readExpensesCollapsed(): Set<string> {
  if (typeof window === "undefined") return new Set();
  const raw = window.localStorage.getItem(EXPENSES_COLLAPSED_KEY);
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((v) => typeof v === "string")) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Per-settlement collapsed state for the Expenses section. Defaults to
 * expanded; the user collapses once they're done reviewing and the choice is
 * remembered across visits.
 */
export function useExpensesCollapsed(settlementId: string) {
  const [collapsed, setCollapsedState] = useState<Set<string>>(readExpensesCollapsed);

  const setCollapsed = useCallback(
    (next: boolean) => {
      setCollapsedState((prev) => {
        if (next === prev.has(settlementId)) return prev;
        const updated = new Set(prev);
        if (next) updated.add(settlementId);
        else updated.delete(settlementId);
        window.localStorage.setItem(EXPENSES_COLLAPSED_KEY, JSON.stringify(Array.from(updated)));
        return updated;
      });
    },
    [settlementId],
  );

  const toggle = useCallback(() => {
    setCollapsedState((prev) => {
      const updated = new Set(prev);
      if (updated.has(settlementId)) updated.delete(settlementId);
      else updated.add(settlementId);
      window.localStorage.setItem(EXPENSES_COLLAPSED_KEY, JSON.stringify(Array.from(updated)));
      return updated;
    });
  }, [settlementId]);

  return { collapsed: collapsed.has(settlementId), setCollapsed, toggle };
}

export function useCurrency() {
  const [currency, setCurrencyState] = useState<Currency>(readCurrency);

  const set = useCallback((next: Currency) => {
    window.localStorage.setItem(CURRENCY_KEY, next);
    setCurrencyState(next);
    // Notify formatCurrency callers that don't go through this hook.
    window.dispatchEvent(new CustomEvent("fellesen:currency-changed"));
  }, []);

  return { currency, setCurrency: set };
}

/**
 * Snapshot the currently-selected currency. Used by `formatCurrency` so
 * non-React call-sites (and components that don't take currency as a prop)
 * still respect the preference.
 */
export function getCurrentCurrency(): Currency {
  return readCurrency();
}
